import { createId } from "@paralleldrive/cuid2";

import type { EditableComponent } from "./coach-attributed-score";

/**
 * Skills the résumé already demonstrates but does not list.
 *
 * WHY THIS IS A SEPARATE MECHANISM: every other suggestion is "replace the text at this fieldPath",
 * applied with lodash `_set`. That cannot express "append an item to the skills list", so until now
 * the optimizer had no way to add a skill — and adding a skill is the most direct lever on
 * `skillOverlap`, the heaviest résumé-movable component (weight 30 under Table B).
 *
 * WHY IT IS NOT FABRICATION, and where the line is: an addition is only allowed when the résumé's
 * own narrative already evidences the skill. A QA résumé whose experience says "conducted
 * regression and smoke testing before production releases" genuinely has Regression Testing — if
 * the skills list omits it, both a human reader and the scorer undercount the candidate. Surfacing
 * that is accurate.
 *
 * What is NOT allowed is adding a skill the résumé says nothing about. That is the keyword stuffing
 * that reached a student earlier in this feature's life — "Skilled in product management,
 * stakeholder management" inserted into a QA summary with no work behind it. So every addition must
 * carry `evidence`: the text on the résumé that supports it. No evidence, no addition.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

/**
 * How well the résumé backs this skill up.
 *
 * `evidenced` — the résumé's own text already demonstrates it; adding it only surfaces what is
 *               there. Safe to recommend outright.
 * `unverified` — the job asks for it and it is plausibly adjacent to what the student has done,
 *               but the résumé does not show it. Offered as a QUESTION, never applied silently,
 *               and labelled so the student confirms it is true before it goes on their résumé.
 */
export type SkillConfidence = "evidenced" | "unverified";

export type SkillAddition = {
  /** Stable id for accept/reject bookkeeping. */
  id: string;
  name: string;
  /** Grouping label shown beside the skill, matching the résumé's existing convention. */
  description: string;
  /** Résumé text supporting it, or — for an unverified proposal — why the job needs it. */
  evidence: string;
  confidence: SkillConfidence;
  targets: EditableComponent;
  gain: number;
};

type SkillItem = { id?: string; name?: string; visible?: boolean };

type ResumeDataLike = {
  sections?: { skills?: { items?: SkillItem[] } } & Record<string, unknown>;
};

const normalize = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * Does the résumé's own text support this skill?
 *
 * Token containment rather than exact phrase: the évidence is prose, so "regression and smoke
 * testing" must satisfy the skill "Regression Testing". Every significant word of the skill name
 * has to appear somewhere in the résumé — that is a low bar deliberately, because the model also
 * has to supply `evidence` and the student still reviews the card, but it is enough to stop a skill
 * the résumé never mentions.
 */
export const isEvidencedByResume = (skillName: string, resumeText: string): boolean => {
  const haystack = normalize(resumeText);
  if (!haystack) return false;

  const words = normalize(skillName)
    .split(" ")
    .filter((word) => word.length > 2);

  if (words.length === 0) return false;

  return words.every((word) => haystack.includes(word));
};

/**
 * Sort proposed skills into what the résumé already proves and what the student must confirm.
 *
 * NOT a filter on whether the job needs the skill — that was the mistake in the first version. It
 * dropped every skill the résumé did not literally mention, which for a QA résumé against a product
 * job meant dropping everything and telling the student there was "nothing safe to rewrite". That
 * defeats the purpose: the tool exists to show a student what they could ADD to score higher.
 *
 * So a skill the job wants but the résumé does not evidence is kept and marked `unverified`. The
 * student is asked, not told — they know whether they have run discovery with stakeholders or owned a
 * backlog, and the résumé cannot know it. Only duplicates and nameless entries are discarded.
 *
 * The honesty guarantee moves from "we refuse to mention it" to "we never apply it without the
 * student saying yes, and we label it as their call". That is the right place for the guarantee:
 * a suggestion is a question, and only the student can answer it.
 *
 * @param resumeText flattened résumé text, used as the evidence corpus
 * @param existingSkillNames current skill names, so nothing is duplicated
 */
export const classifySkillAdditions = (
  additions: SkillAddition[],
  resumeText: string,
  existingSkillNames: string[],
) => {
  const already = existingSkillNames.map(normalize).filter(Boolean);
  const accepted: SkillAddition[] = [];
  const rejected: Array<{ addition: SkillAddition; reason: string }> = [];

  /**
   * Already on the résumé, allowing for one name containing the other.
   *
   * Exact matching is not enough: the model proposed adding "API Testing" to a résumé that already
   * lists "API Testing using Postman". Exact comparison says they differ, so the card would offer a
   * redundant skill — and claim a score gain the scorer almost certainly already credits, making
   * the projection wrong in the direction that flatters us.
   */
  const isAlreadyListed = (name: string) => {
    const candidate = normalize(name);
    if (!candidate) return false;

    return already.some((existing) => existing.includes(candidate) || candidate.includes(existing));
  };

  for (const addition of additions) {
    const name = addition.name?.trim();

    if (!name) {
      rejected.push({ addition, reason: "no skill name" });
      continue;
    }
    // The only hard rejection left. A duplicate wastes the student's attention and claims a gain the
    // scorer already credits, which would make the projection flatter us.
    if (isAlreadyListed(name)) {
      rejected.push({ addition, reason: `${name} is already covered by a listed skill` });
      continue;
    }

    accepted.push({
      ...addition,
      name,
      // Recomputed rather than trusted: the model has an incentive to call everything evidenced,
      // and this label is what tells the student whether they need to think before accepting.
      confidence: isEvidencedByResume(name, resumeText) ? "evidenced" : "unverified",
    });
  }

  return {
    accepted,
    evidenced: accepted.filter((entry) => entry.confidence === "evidenced"),
    unverified: accepted.filter((entry) => entry.confidence === "unverified"),
    rejected,
  };
};

/**
 * Shape of one skills item, matching libs/schema skillSchema.
 *
 * `id` is a real cuid2 because `PATCH /resume/:id` validates every item id against
 * `z.string().cuid2()` and rejects the whole document if one fails.
 */
export const buildSkillItem = (addition: SkillAddition) => ({
  id: createId(),
  visible: true,
  name: addition.name.trim(),
  description: addition.description?.trim() || "",
  level: 0,
  keywords: [],
});

/**
 * Append the accepted additions to the skills section.
 *
 * Returns a new document; never mutates. Keyed by name so applying twice cannot duplicate a skill —
 * the panel can re-derive the résumé from the accepted set without accumulating.
 */
export const applySkillAdditions = <T extends ResumeDataLike>(
  resumeData: T,
  additions: SkillAddition[],
): T => {
  if (additions.length === 0) return resumeData;

  const next = JSON.parse(JSON.stringify(resumeData)) as T;
  const skills = next.sections?.skills;
  if (!skills || !Array.isArray(skills.items)) return resumeData;

  const present = new Set(skills.items.map((item) => normalize(String(item?.name ?? ""))));

  for (const addition of additions) {
    if (present.has(normalize(addition.name))) continue;
    skills.items.push(buildSkillItem(addition));
    present.add(normalize(addition.name));
  }

  return next;
};

/**
 * Remove skills by name — the inverse of applying, for reject.
 *
 * By name rather than by the addition's id: the item that landed on the résumé carries its own
 * generated cuid2, and matching on the addition id would silently fail to remove anything.
 */
export const removeSkillsByName = <T extends ResumeDataLike>(resumeData: T, names: string[]): T => {
  if (names.length === 0) return resumeData;

  const next = JSON.parse(JSON.stringify(resumeData)) as T;
  const skills = next.sections?.skills;
  if (!skills || !Array.isArray(skills.items)) return resumeData;

  const remove = new Set(names.map(normalize));
  skills.items = skills.items.filter((item) => !remove.has(normalize(String(item?.name ?? ""))));

  return next;
};
